import type { TreeOption } from 'naive-ui';
import { FeatureTreePresentationIndex } from './FeatureTreePresentationIndex';

/**
 * 在稳定父节点下替换一组运行时直接子树。
 *
 * 本对象只维护纯 UI 树与元数据映射，不读取 GLB hierarchy、不请求 Worker，也不持有 Object3D。
 */
export class FeatureTreeRuntimeSubtreeProjector {
  /**
   * 应用一次运行时子树替换。
   *
   * @param tree 当前激活或待提交的树投影。
   * @param nodeMap 与树配套的可变元数据映射。
   * @param index 当前树的稳定节点索引。
   * @param update 已完成领域构建的子树更新。
   * @returns 父节点存在并已应用时返回 true。
   */
  public apply<TNodeMeta>(
    tree: readonly TreeOption[],
    nodeMap: Map<string, TNodeMeta>,
    index: FeatureTreePresentationIndex,
    update: GlbRuntimeSubtreeUpdate<TNodeMeta>,
  ): boolean {
    const parent = index.resolve(tree, update.parentKey);
    if (!parent) return false;

    const removedRootKeys = new Set(update.removedRootKeys);
    // 完整刷新可能已经包含本次新增节点；按稳定 key 替换同名旧投影，保证重放幂等。
    const insertedChildren = update.childInsertions?.length
      ? update.childInsertions.map(insertion => insertion.child)
      : update.children;
    for (const child of insertedChildren) {
      const childKey = this.resolveNodeKey(child);
      if (childKey) removedRootKeys.add(childKey);
    }
    const existingChildren = Array.isArray(parent.children) ? parent.children : [];
    const removedNodeKeys = [...update.removedNodeKeys];
    for (const child of existingChildren) {
      const childKey = this.resolveNodeKey(child);
      if (!childKey || !removedRootKeys.has(childKey)) continue;
      this.collectSubtreeKeys(child, removedNodeKeys);
    }

    const retainedChildren = existingChildren.filter((child) => {
      const childKey = this.resolveNodeKey(child);
      return !childKey || !removedRootKeys.has(childKey);
    });
    const nextChildren = [...retainedChildren];
    if (update.childInsertions && update.childInsertions.length > 0) {
      /**
       * parentIndex 表示最终兄弟序列位置。先移除旧根，再按索引升序逐项插入，后续索引即可
       * 直接相对于已经恢复的前序兄弟计算；稳定排序同时保留相同索引输入的领域顺序。
       */
      const orderedInsertions = [...update.childInsertions]
        .sort((left, right) => left.index - right.index);
      for (const insertion of orderedInsertions) {
        const boundedIndex = Math.min(
          Math.max(0, Math.floor(insertion.index)),
          nextChildren.length,
        );
        nextChildren.splice(boundedIndex, 0, insertion.child);
      }
    } else {
      nextChildren.push(...update.children);
    }
    parent.children = nextChildren.length > 0 ? nextChildren : undefined;
    parent.isLeaf = nextChildren.length === 0;

    for (const key of new Set(removedNodeKeys)) nodeMap.delete(key);
    update.nodeMapEntries.forEach((meta, key) => nodeMap.set(key, meta));
    // children 已改变，旧索引不能继续定位已移除节点。
    index.clear();
    return true;
  }

  /**
   * 收集一棵旧子树的全部稳定 key。
   *
   * @param root 旧子树根。
   * @param target 调用方复用的输出数组。
   */
  private collectSubtreeKeys(root: TreeOption, target: string[]): void {
    const pending = [root];
    while (pending.length > 0) {
      const node = pending.pop()!;
      const key = this.resolveNodeKey(node);
      if (key) target.push(key);
      if (!Array.isArray(node.children)) continue;
      for (let index = node.children.length - 1; index >= 0; index -= 1) {
        pending.push(node.children[index]);
      }
    }
  }

  /**
   * 规范化 NaiveUI 节点 key。
   *
   * @param node 目标树节点。
   * @returns 字符串 key；节点无 key 时返回空串。
   */
  private resolveNodeKey(node: TreeOption): string {
    return typeof node.key === 'string' || typeof node.key === 'number'
      ? String(node.key)
      : '';
  }
}
